import React from 'react';
import styled, { withTheme, css, DefaultTheme } from 'styled-components';
import { FjAvatar, FjEllipsis, FjTooltip } from 'components/primitives';
import { EditableInput, DatePicker } from 'components/styled-primitives';

import SortIcon from 'components/table/sort-icon';

// utils is an untyped .js module; its exports resolve as any.
import { getUserInitials, getCostCenterInitials, formatDate, formatTime } from 'utils';

interface SortState {
  columnName?: string;
  id?: string;
  desc?: boolean;
}

interface CellProps extends React.HTMLAttributes<HTMLDivElement> {
  alignLeft?: boolean;
  noPadding?: boolean;
  clickable?: boolean;
  capitalize?: boolean;
  dataTestId?: string;
}

const clickableCSS = css`
  cursor: pointer;

  button {
    cursor: pointer !important;
  }
`;

export const Cell = styled.div.attrs<CellProps>(({ dataTestId }) => ({
  dataTestId: dataTestId || undefined,
}))`
  flex: 1 1 auto;
  display: flex;
  width: 100%;
  text-align: ${(props) => (props.alignLeft ? 'left' : 'center')};
  justify-content: ${(props) => (props.alignLeft ? 'start' : 'center')};
  align-items: center;
  padding: 0 ${({ noPadding }) => (noPadding ? 0 : 5)}px;
  ${({ clickable }) => (clickable ? clickableCSS : '')};
  text-transform: ${(props) => (props.capitalize ? 'capitalize' : '')};
`;

interface HeaderCellProps extends CellProps {
  text?: React.ReactNode;
  sortState: SortState;
  columnName: string;
  children?: React.ReactNode;
  sortable?: boolean;
  wrapHeader?: boolean;
}

const HeaderText = styled.div<{ wrapHeader?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  overflow: ${({ wrapHeader }) => (wrapHeader ? 'visible' : 'hidden')};
  white-space: ${({ wrapHeader }) => (wrapHeader ? 'normal' : 'nowrap')};
  text-overflow: ${({ wrapHeader }) => (wrapHeader ? 'unset' : 'ellipsis')};
  line-height: 1.5;
  padding: 2px 0;
  word-break: ${({ wrapHeader }) => (wrapHeader ? 'break-word' : 'normal')};
`;

export const HeaderCell = ({
  text,
  sortState,
  columnName,
  alignLeft,
  children,
  sortable = true,
  wrapHeader = false,
  ...props
}: HeaderCellProps): React.ReactElement => (
  <Cell alignLeft={alignLeft} {...props}>
    <HeaderText wrapHeader={wrapHeader}>{text || children}</HeaderText>
    {sortable && <SortIcon sortState={sortState} columnName={columnName} />}
  </Cell>
);

export const CheckboxCell = styled(Cell)`
  text-align: left;
  justify-content: center;
  margin-left: 7px;
`;

/* Avatar Cell */
const AvatarCellContainer = styled.div`
  flex: 0 0 auto;
  width: fit-content;
  display: flex;
  align-items: center;
  :hover {
    cursor: pointer;
  }
`;

const MainText = styled.span`
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  color: inherit;
  margin: 0;
  justify-content: inherit;
`;

const Subtext = styled.span`
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  color: ${({ theme }) => theme.colors.notSelectable};
  margin: 2px 0 0 0;
  justify-content: inherit;
`;

const TextContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
  margin-left: 15px;
`;

const DateTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

interface AvatarCellProps extends CellProps {
  name?: React.ReactNode;
  email?: React.ReactNode;
  initials?: string;
  clickable?: boolean;
  theme: DefaultTheme;
  // Rendered as its own line below name/email, inside the same flex column — kept as a
  // separate slot (rather than folded into `name`) so callers needing a nested element
  // (e.g. NameCell's phantom-account tooltip, which renders a <div>) don't nest block content
  // inside MainText/Subtext, which are <span>s.
  badge?: React.ReactNode;
}

const AvatarCellBase = ({
  name,
  email,
  initials,
  clickable,
  theme,
  badge,
  ...props
}: AvatarCellProps): React.ReactElement => (
  <Cell className="AvatarCellBase" alignLeft clickable={clickable} {...props}>
    <FjEllipsis>
      <AvatarCellContainer>
        <FjAvatar
          initials={initials || ''}
          boxSize={30}
          // Partial style (no fontWeight) preserves the original runtime; cast to
          // the required FjAvatarStyle shape without adding an unused property.
          fjAvatarStyle={
            {
              fontSize: 'inherit',
              color: theme.colors.white,
              backgroundColor: theme.colors.notSelectable,
            } as React.ComponentProps<typeof FjAvatar>['fjAvatarStyle']
          }
        />
        <TextContainer>
          {name && <MainText>{name}</MainText>}
          {email && <Subtext>{email}</Subtext>}
          {badge}
        </TextContainer>
      </AvatarCellContainer>
    </FjEllipsis>
  </Cell>
);

export const AvatarCell = withTheme(AvatarCellBase);

const PhantomBadge = styled.span`
  display: inline-block;
  align-self: flex-start;
  margin-top: 2px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.notSelectable};
  background: rgba(142, 174, 178, 0.16);
  border-radius: 10px;
  white-space: nowrap;
`;

const PhantomTooltipContent = styled.span`
  display: inline-block;
  max-width: 220px;
`;

interface NameCellProps extends CellProps {
  firstname?: string;
  lastname?: string;
  email?: string;
  onClick?: () => void;
  // True when the owner hasn't created/verified their own Portal account yet (a SuperAdmin
  // or an integration can create a cost center/license for someone by email alone) — any
  // name shown may just be a placeholder entered on their behalf until then.
  phantom?: boolean;
}

export const NameCell = ({
  firstname,
  lastname,
  email,
  onClick,
  phantom,
  ...props
}: NameCellProps): React.ReactElement => {
  const initials = getUserInitials({ firstname, lastname }) || '?';
  const name = firstname && lastname ? `${firstname} ${lastname}` : firstname || lastname;
  return (
    <AvatarCell
      name={name || email}
      email={name ? email : null}
      initials={initials}
      clickable={!!onClick}
      onClick={() => onClick && onClick()}
      badge={
        phantom ? (
          <FjTooltip
            dataTestId="name-cell-phantom"
            Component={
              <PhantomTooltipContent>
                This name may be a placeholder entered by an admin or an integration — the
                owner hasn&apos;t created or verified their own Portal account yet.
              </PhantomTooltipContent>
            }
          >
            <PhantomBadge>Awaiting account setup</PhantomBadge>
          </FjTooltip>
        ) : null
      }
      {...props}
    />
  );
};

interface CostCenterCellProps extends CellProps {
  email?: string;
  costCenter?: string;
  onClick?: () => void;
}

export const CostCenterCell = ({
  email,
  costCenter,
  onClick,
  ...props
}: CostCenterCellProps): React.ReactElement => {
  const initials = getCostCenterInitials(costCenter, 3) || '?';
  return (
    <AvatarCell
      name={costCenter}
      email={email}
      initials={initials}
      clickable={!!onClick}
      onClick={() => onClick && onClick()}
      {...props}
    />
  );
};

export const TableEditableInput = styled(EditableInput).attrs({
  displayEditOnHover: true,
})`
  align-self: stretch;
`;

export const TableDatePicker = styled(DatePicker).attrs((cellProps) => ({
  inline: true,
  displayEditOnHover: true,
  dateContainer: ({ children, ...props }: { children?: React.ReactNode }) => (
    // cellProps are forwarded styled-component props; spread verbatim as in the
    // original JS. Cast at this boundary since their shape is dynamic.
    <DateTimeCell
      value={children}
      noPadding
      clickable
      {...props}
      {...(cellProps as unknown as DateTimeCellProps)}
    />
  ),
}))`
  align-self: stretch;
`;

interface DateTimeCellProps extends CellProps {
  value?: React.ReactNode;
}

export const DateTimeCell = ({ value, ...props }: DateTimeCellProps): React.ReactElement => (
  <Cell {...props}>
    <DateTimeContainer>
      <MainText>{formatDate(value as string | number | Date | null | undefined)}</MainText>
      <Subtext>{formatTime(value as string | number | Date | null | undefined)}</Subtext>
    </DateTimeContainer>
  </Cell>
);

interface DatePeriodCellProps extends CellProps {
  startDate?: Date | string;
  endDate?: Date | string;
}

export const DatePeriodCell = ({
  startDate,
  endDate,
  ...props
}: DatePeriodCellProps): React.ReactElement => {
  const hasEndDate = !!formatTime(endDate);
  const endDateStyle: React.CSSProperties = hasEndDate
    ? {}
    : { opacity: 0, pointerEvents: 'none' };
  const endDateWithDefault = hasEndDate ? endDate : new Date();
  return (
    <Cell {...props}>
      <DateTimeContainer>
        <MainText>{formatDate(startDate)}</MainText>
        <Subtext>{formatTime(startDate)}</Subtext>
      </DateTimeContainer>
      <span>&nbsp;&nbsp;&rarr;&nbsp;&nbsp;</span>
      <div style={endDateStyle}>
        <MainText>{formatDate(endDateWithDefault)}</MainText>
        <Subtext>{formatTime(endDateWithDefault)}</Subtext>
      </div>
    </Cell>
  );
};
